import { BasicModalProps, FlexDirection, IDropzoneFile } from '@common/types/common';
import { Dialog } from '@components/display/dialogs';
import { FileUpload } from '@components/utility/files';
import { EventFormikType, EventTypes } from '@event/types/event';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CheckRounded from '@mui/icons-material/CheckRounded';
import Edit from '@mui/icons-material/Edit';
import UploadFile from '@mui/icons-material/UploadFile';
import { Alert, Button, Typography } from '@mui/material';
import { readFileAsDataURL, toaster } from '@utils/common';
import { ClassValue } from 'clsx';
import { FormikProps } from 'formik';
import { useCallback, useState } from 'react';

type FilePickerModal = BasicModalProps & {
    fileUploadContainerClassName?: ClassValue;
    modalClassName?: ClassValue;
    formik: FormikProps<EventFormikType>;
    eventType: EventTypes;
    eventId: string;
    setPreview: (value: string | null) => void;
};

const FilePickerModal = ({
    open,
    setOpen,
    formik,
    fileUploadContainerClassName,
    modalClassName,
    setPreview,
}: FilePickerModal) => {
    const [success, setSuccess] = useState(false);

    const fileDropzoneAreaTitleElement = (
        <>
            <span className="text-primary-400 underline decoration-primary-100 cursor-pointer text-base tracking-wider">
                Click
            </span>{' '}
            <span className="text-neutral-950 font-normal text-base tracking-wider">or drag and drop</span>
        </>
    );

    const handleClose = useCallback(() => {
        setSuccess(false);
    }, [setSuccess]);

    return (
        <Dialog
            dialogClassName={modalClassName}
            {...{ open, setOpen }}
            customCloseHandler={handleClose}
            header={
                <div className="flex flex-col items-center">
                    {!success && (
                        <div className="text-center mt-24">
                            <Typography className="font-museo" variant="h4" fontWeight={400}>
                                Edit Image
                            </Typography>
                            <Typography className="font-inter" variant="body2" fontWeight={400}>
                                Upload the image you want to be displayed in the news article card.
                            </Typography>
                        </div>
                    )}
                    <Alert className="max-w-[90%] mt-4" severity="warning">
                        A Banner image can be uploaded but it will not be displayed on the public event page.
                        <br />
                        In the future it will be displayed on the public event page seen by your attendees and audience.
                    </Alert>
                </div>
            }
            actions={
                <div className="flex flex-row w-full h-max items-center justify-arround align-middle px-16 pb-16 gap-7">
                    {success ? (
                        <Button
                            startIcon={<Edit />}
                            onClick={() => {
                                setOpen(false);
                                handleClose();
                            }}
                            className="rounded-full w-full"
                        >
                            Continue Editing
                        </Button>
                    ) : (
                        <>
                            <Button
                                startIcon={<ArrowBack />}
                                color="secondary-alt"
                                fullWidth
                                className="rounded-full black-border"
                                variant="contained"
                                onClick={() => {
                                    setPreview(null);
                                    formik.setFieldValue('banner', null);
                                    handleClose();
                                    setOpen(false);
                                }}
                            >
                                Go Back
                            </Button>
                            <Button
                                disabled={!formik.values.banner}
                                startIcon={<CheckCircleOutline />}
                                className="rounded-full w-full"
                                onClick={() => {
                                    if (formik.values.banner) {
                                        readFileAsDataURL(formik.values.banner[0].file).then((value: string) => {
                                            try {
                                                setPreview(
                                                    `data:${
                                                        (formik.values.banner as IDropzoneFile[])[0].file.type
                                                    };${value}`
                                                );
                                                setSuccess(true);
                                            } catch (err) {
                                                toaster.failure({
                                                    msg: 'There was an error when setting the preview for the banner',
                                                });
                                            }
                                        });
                                    }
                                }}
                            >
                                Confirm
                            </Button>
                        </>
                    )}
                </div>
            }
        >
            {success ? (
                <div className="flex flex-col items-center w-full justify-center text-center mb-16 mt-20">
                    <div className="flex items-center justify-center rounded-full border-neutral-700 border-2 size-24">
                        <CheckRounded className="size-14" />
                    </div>
                    <Typography className="font-museo" variant="h2" fontWeight={600}>
                        Success
                    </Typography>
                    <Typography className="font-inter w-3/5" variant="h5">
                        The banner preview has been updated successfully
                    </Typography>
                </div>
            ) : (
                <FileUpload
                    {...{
                        containerClassName: fileUploadContainerClassName,
                        fileDropzoneAreaProps: {
                            dropzoneClasses: '!py-10',
                            multiple: false,
                            accept: { 'image/png': [], 'image/jpeg': [] },
                            title: fileDropzoneAreaTitleElement,
                            icon: <UploadFile color="primary-600" />,
                            subtextClasses: 'text-sm mt-2',
                        },
                        maxFiles: 1,
                        fileFieldName: 'banner',
                        formik,
                        flexDirection: { default: FlexDirection.COL, md: FlexDirection.COL },
                        uploadFileIcon: <UploadFile />,
                    }}
                />
            )}
        </Dialog>
    );
};

export { FilePickerModal };
